import type { ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { Tooltip } from "@/components/atoms/Tooltip";

interface Props {
  name: string;
  typeLabel: string;
  icon: ReactNode;
  expanded: boolean;
  onToggleExpand: () => void;
  /** Credentials saved — setup complete. */
  configured: boolean;
  /** Channel active — follows the enable toggle once conversations are enabled. */
  connected: boolean;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  toggleDisabled?: boolean;
  toggleDisabledTitle?: string;
  variant?: "le" | "branch";
}

function ConfiguredPill({ configured }: { configured: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap",
        configured
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-slate-100 text-slate-500 border-slate-200",
      )}
    >
      {configured ? "Configured" : "Not configured"}
    </span>
  );
}

function StatusLabel({ active, label, inactiveLabel }: { active: boolean; label: string; inactiveLabel: string }) {
  return (
    <span className={cn("text-xs font-medium whitespace-nowrap", active ? "text-emerald-600" : "text-bridge-muted")}>
      {active ? label : inactiveLabel}
    </span>
  );
}

export function ChannelEntityCardHeader({
  name,
  typeLabel,
  icon,
  expanded,
  onToggleExpand,
  configured,
  connected,
  enabled,
  onEnabledChange,
  toggleDisabled = false,
  toggleDisabledTitle,
  variant = "le",
}: Props) {
  const toggle = (
    <label
      className={cn(
        "relative inline-flex items-center",
        toggleDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer select-none",
      )}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={enabled}
        disabled={toggleDisabled}
        onChange={(e) => {
          if (!toggleDisabled) onEnabledChange(e.target.checked);
        }}
      />
      <div
        className={cn(
          "block h-6 w-11 rounded-full transition-colors duration-200 ease-in-out",
          enabled ? "bg-emerald-500" : "bg-slate-300",
        )}
      />
      <div
        className={cn(
          "absolute left-1 top-1 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out",
          enabled ? "translate-x-5" : "",
        )}
      />
    </label>
  );

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 border-b border-bridge-border",
        variant === "le" ? "bg-slate-50 px-5 py-3" : "bg-white px-5 py-2.5 border-bridge-border/40",
      )}
    >
      <button
        type="button"
        onClick={onToggleExpand}
        className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
        aria-expanded={expanded}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-bridge-muted">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        {icon}
        <div className={cn("min-w-0", variant === "branch" && "pl-0")}>
          <span
            className={cn(
              "block truncate font-semibold text-bridge-fg",
              variant === "branch" ? "text-xs" : "text-sm",
            )}
          >
            {name}
          </span>
          <span
            className={cn(
              "block text-bridge-muted font-bold uppercase tracking-wider",
              variant === "branch" ? "text-[9px]" : "text-[10px]",
            )}
          >
            {typeLabel}
          </span>
        </div>
      </button>

      <div className="flex shrink-0 items-center gap-3">
        <ConfiguredPill configured={configured} />

        <span className="h-4 w-px bg-bridge-border/70" aria-hidden />

        <div className="flex items-center gap-2">
          <StatusLabel active={connected} label="Connected" inactiveLabel="Not connected" />
          {toggleDisabled && toggleDisabledTitle ? (
            <Tooltip content={toggleDisabledTitle} side="top">
              {toggle}
            </Tooltip>
          ) : (
            toggle
          )}
        </div>
      </div>
    </div>
  );
}
